import WrapperContainer from "@components/WrapperContainer";
import ContentContainer from "@src/components/ContentContainer";
import PageTopSection from "@src/components/PageTopSection";
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import {
  PlaylistOverviewHeader,
  PlaylistOverviewHeaderLeft,
  PlaylistOverviewHeaderRight,
  ButtonSection,
  TotalLabel,
  TotalLabelWrapper,
  SearchContainer,
  SearchInputWrapper,
} from "../GuestCurator/GuestCurator.styles";
import { SearchInput } from "@src/components/SearchInput";
import PageTitle from "@src/components/PageTitle";
import CompactIcon from "@src/assets/img/compacts-icon.svg";
import CompactIconActive from "@src/assets/img/compacts-icon-active.svg";
import CuratorManagementList from "@src/components/CuratorManagementList";
import { useSettings } from "@src/context/SettingsContext";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import ErrorLabel from "@src/components/ErrorLabel";
import Spinner from "@src/components/Spinner";
import { NoResultsFoundMessage } from "@src/components/NoResultsFound";
import { CompactActionButton } from "@src/components/CompactActionButton";
import {
  useInfiniteActiveCuratorsQuery,
  useUpdateGuestCuratorMutation,
} from "@src/queries/curatorQueries";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@src/queries/queryKeys";
import { useRole } from "@src/hooks/useAuth";
import { Navigate } from "react-router-dom";
import { IActiveCuratorWithArchivedCount } from "@src/apis/curatorApis";
import { SortingParams } from "@src/types/httpApi";
import { AxiosResponse } from "axios";
import { ApiResponse } from "@src/types/httpApi";
import { theme } from "@src/theme";
import { useNotification } from "@src/context/NotificationContext";
import ContentDrawer from "@src/components/ContentDrawer";
import GuestCuratorForm from "../GuestCurator/GuestCuratorForm";
import { CreateGuestCurator } from "@src/types/curator";
import { IShowRecord } from "@src/types/show";

const schema = yup.object().shape({
  searchText: yup
    .string()
    .max(50, "Search text must be less than 50 characters"),
});

type tracksFiltersAppliedType = {
  searchText?: string;
};

const RenderContentStatus = ({
  loading,
  results,
}: {
  loading: boolean;
  results: number;
}): React.JSX.Element | null => {
  if (results) return <></>;
  if (loading) return <Spinner />;
  if (!loading && !results)
    return <NoResultsFoundMessage message="No curators found" />;
  return null;
};

const CuratorManagement = (): React.JSX.Element => {
  const role = useRole();
  if (role !== "admin" && role !== "manager") {
    return <Navigate to="/" />;
  }

  const { addNotification } = useNotification();
  const [curatorToEdit, setCuratorToEdit] = useState<CreateGuestCurator | null>(
    null
  );
  const [isUpdateCuratorDrawerOpen, setIsUpdateCuratorDrawerOpen] =
    React.useState<boolean>(false);
  const [requestingApi, setRequestingApi] = useState(false);

  const {
    toggleGuestCuratorsTable,
    tableSettings: { guestCuratorsTable },
  } = useSettings();
  const [filtersApplied, setFiltersApplied] =
    useState<tracksFiltersAppliedType>({
      searchText: "",
    });
  const [sorting, setSorting] = useState<SortingParams | undefined>({
    field: "archivedPlaylistCount",
    direction: "desc",
  });

  const tableEndRef = useRef<HTMLTableRowElement | null>(null);

  const {
    data: curatorsData,
    isLoading: fetchingCurators,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteActiveCuratorsQuery(filtersApplied.searchText, 25, sorting, {
    enabled: true,
  });

  const handleSort = useCallback(
    ({
      accessor,
      sort,
    }: {
      accessor: string;
      sort: "" | "asc" | "desc";
    }): void => {
      if (!sort) {
        setSorting(undefined);
        return;
      }
      setSorting({ field: accessor, direction: sort });
    },
    []
  );

  const curators = useMemo(() => {
    if (!curatorsData?.pages) return [];
    const allCurators: IActiveCuratorWithArchivedCount[] = [];
    curatorsData.pages.forEach((page) => {
      const response = page as AxiosResponse<
        ApiResponse<IActiveCuratorWithArchivedCount[]>
      >;
      if (response?.data?.data) {
        allCurators.push(...response.data.data);
      }
    });
    return allCurators;
  }, [curatorsData]);

  const totalCount = useMemo(() => {
    if (!curatorsData?.pages?.[0]) return 0;
    const firstPage = curatorsData.pages[0] as AxiosResponse<
      ApiResponse<IActiveCuratorWithArchivedCount[]>
    >;
    return firstPage?.data?.meta?.pagination?.total || 0;
  }, [curatorsData]);

  // Use archivedPlaylistCount directly from backend response
  // Backend should include this field in the curator response
  const curatorsWithCounts = useMemo(() => {
    return curators.map((curator) => ({
      ...curator,
      archivedPlaylistCount: curator.archivedPlaylistCount ?? 0,
    }));
  }, [curators]);

  // Infinite scroll observer
  const isFetchingRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastFetchTimeRef = useRef<number>(0);

  useEffect(() => {
    // Reset fetching flag when search text changes (query resets)
    isFetchingRef.current = false;
    lastFetchTimeRef.current = 0;

    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    // Don't set up observer if:
    // - No ref element
    // - Currently loading initial data
    if (!tableEndRef.current || fetchingCurators) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        const now = Date.now();
        // Throttle: prevent requests if last fetch was less than 1000ms ago
        const timeSinceLastFetch = now - lastFetchTimeRef.current;

        // Multiple safety checks to prevent duplicate requests
        if (
          target.isIntersecting &&
          hasNextPage &&
          !isFetchingNextPage &&
          !isFetchingRef.current &&
          !fetchingCurators &&
          timeSinceLastFetch > 1000 // Throttle: minimum 1 second between requests
        ) {
          // Immediately set flag and disconnect observer to prevent multiple triggers
          isFetchingRef.current = true;
          lastFetchTimeRef.current = now;

          if (observerRef.current) {
            observerRef.current.disconnect();
          }

          fetchNextPage().finally(() => {
            isFetchingRef.current = false;
            // Only re-observe if conditions are met and ref is still valid
            // Wait longer before re-observing to prevent rapid re-triggering
            if (
              hasNextPage &&
              tableEndRef.current &&
              !fetchingCurators &&
              !isFetchingNextPage
            ) {
              setTimeout(() => {
                if (
                  observerRef.current &&
                  tableEndRef.current &&
                  hasNextPage &&
                  !isFetchingNextPage &&
                  !fetchingCurators &&
                  !isFetchingRef.current
                ) {
                  observerRef.current.observe(tableEndRef.current);
                }
              }, 1000);
            }
          });
        }
      },
      { root: null, rootMargin: "200px", threshold: 0.1 }
    );

    observerRef.current = observer;
    const currentRef = tableEndRef.current;

    // Only observe if not currently fetching
    if (!isFetchingNextPage) {
      observer.observe(currentRef);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      isFetchingRef.current = false;
    };
  }, [fetchNextPage, hasNextPage, fetchingCurators]);

  // Separate effect to handle observer when fetching state changes
  useEffect(() => {
    if (!observerRef.current || !tableEndRef.current) return;

    if (isFetchingNextPage) {
      // Disconnect observer while fetching to prevent multiple triggers
      observerRef.current.disconnect();
    } else if (hasNextPage && !fetchingCurators && !isFetchingRef.current) {
      // Re-observe when fetching completes (if conditions are met)
      const currentRef = tableEndRef.current;
      if (currentRef) {
        // Wait before re-observing to prevent immediate re-trigger
        const timeoutId = setTimeout(() => {
          if (
            observerRef.current &&
            tableEndRef.current &&
            hasNextPage &&
            !isFetchingNextPage &&
            !fetchingCurators &&
            !isFetchingRef.current
          ) {
            observerRef.current.observe(tableEndRef.current);
          }
        }, 1000);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [isFetchingNextPage, hasNextPage, fetchingCurators]);

  const {
    control,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      searchText: "",
    },
  });
  const inputSearchText = watch("searchText");

  useEffect(() => {
    const handler = setTimeout(() => {
      setFiltersApplied((prev) => ({
        ...prev,
        searchText: inputSearchText ? inputSearchText.trim() : "",
      }));
    }, 800);
    return (): void => clearTimeout(handler);
  }, [inputSearchText]);

  const queryClient = useQueryClient();
  const mutationUpdateCurator = useUpdateGuestCuratorMutation();

  const submitUpdateGuestCurator = async (
    data: CreateGuestCurator
  ): Promise<void> => {
    setRequestingApi(true);
    mutationUpdateCurator.mutateAsync(
      {
        name: data.name,
        email: data.email,
        shows: data.shows ?? [],
        about: data.about,
        notes: data.notes,
        desktopPhoto: data.desktopPhoto,
        mobilePhoto: data.mobilePhoto,
        showsDropDown: data.showsDropDown,
        id: curatorToEdit?.id,
        documentId: curatorToEdit?.documentId,
        preserveIsSubcurator: true, // Preserve original isSubcurator value for active curators
      },
      {
        onError: () => addNotification("Failed to update curator", "error"),
        onSuccess: async () => {
          addNotification("Curator updated successfully", "success");
          setIsUpdateCuratorDrawerOpen(false);
          await queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.ACTIVE_CURATORS],
          });
        },
        onSettled: () => setRequestingApi(false),
      }
    );
  };

  const onClickEdit = async (
    data: IActiveCuratorWithArchivedCount
  ): Promise<void> => {
    // Handle both 'show' (object) and 'shows' (array) for backwards compatibility
    const showsArray = data.shows || (data.show ? [data.show] : []);
    // Backend returns flat email; fallback to user.email for backward compatibility
    const email = data.email ?? data.user?.email ?? "";

    setCuratorToEdit({
      name: data.name,
      email,
      shows: showsArray.map((show: IShowRecord) => show.documentId),
      about: data.about || "",
      notes: data.notes ?? "",
      desktopPhoto: null,
      mobilePhoto: null,
      showsDropDown: showsArray
        .map((show: IShowRecord) => show.documentId)
        .join(","),
      documentId: data.documentId,
      id: data.id,
      desktopPhotoUrl:
        data.webCuratorCatalog?.formats?.thumbnail?.url ||
        data.webCuratorCatalog?.url ||
        null,
      mobilePhotoUrl:
        data.webCuratorCatalog?.formats?.thumbnail?.url ||
        data.webCuratorCatalog?.url ||
        null,
    });
    setIsUpdateCuratorDrawerOpen(true);
  };

  return (
    <WrapperContainer>
      <PageTopSection>
        <PlaylistOverviewHeader>
          <PlaylistOverviewHeaderLeft>
            <ButtonSection>
              <PageTitle>Curator Management</PageTitle>
              <div style={{ display: "flex", gap: "16px" }}>
                <CompactActionButton
                  $tooltipContent="Compact view"
                  $active={guestCuratorsTable}
                  onClick={toggleGuestCuratorsTable}
                >
                  <img
                    src={guestCuratorsTable ? CompactIconActive : CompactIcon}
                    alt="compact"
                    style={{ width: "50%", objectFit: "contain" }}
                  />
                </CompactActionButton>
              </div>
            </ButtonSection>
          </PlaylistOverviewHeaderLeft>
          <PlaylistOverviewHeaderRight>
            {fetchingCurators && <Spinner small />}
            <SearchContainer>
              <SearchInputWrapper>
                <ErrorLabel>{errors.searchText?.message}</ErrorLabel>
                <Controller
                  name="searchText"
                  control={control}
                  render={({ field }) => (
                    <SearchInput {...field} placeholder="Search by name" />
                  )}
                />
              </SearchInputWrapper>
              <TotalLabelWrapper>
                <TotalLabel>Total: {totalCount}</TotalLabel>
              </TotalLabelWrapper>
            </SearchContainer>
          </PlaylistOverviewHeaderRight>
        </PlaylistOverviewHeader>
      </PageTopSection>
      <ContentContainer allowScroll={true}>
        <ContentDrawer
          isOpen={isUpdateCuratorDrawerOpen}
          toggleDrawer={() =>
            setIsUpdateCuratorDrawerOpen(!isUpdateCuratorDrawerOpen)
          }
        >
          <GuestCuratorForm
            disableInputs={requestingApi}
            isOpen={isUpdateCuratorDrawerOpen}
            initialValues={curatorToEdit || undefined}
            editMode
            closeButtonHandler={() => setIsUpdateCuratorDrawerOpen(false)}
            fetchingCurators={fetchingCurators}
            submitButtonHandler={submitUpdateGuestCurator}
          />
        </ContentDrawer>
        <div
          style={{ width: "100%", display: "flex", flexDirection: "column" }}
        >
          <CuratorManagementList
            data={curatorsWithCounts}
            loaderRef={tableEndRef}
            onClickEdit={onClickEdit}
            onSortCb={handleSort}
          />
          {isFetchingNextPage && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: theme.margins.medium,
              }}
            >
              <Spinner />
            </div>
          )}
          <RenderContentStatus
            loading={fetchingCurators && curatorsWithCounts.length === 0}
            results={curatorsWithCounts.length}
          />
        </div>
      </ContentContainer>
    </WrapperContainer>
  );
};

export default CuratorManagement;
